import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Users, BookOpen, FileText, GraduationCap, ExternalLink, AlertTriangle } from 'lucide-react';

interface School {
  id: string;
  name: string;
  dfe_number: string | null;
  postcode: string | null;
  plan_tier: string;
  plan_sub_tier: string | null;
  billing_status: string;
  billing_scope: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  billing_email: string | null;
  created_at: string;
  suspended_at: string | null;
  suspension_reason: string | null;
  internal_notes: string | null;
}

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface UsageCounts {
  pupils: number;
  classes: number;
  assessments: number;
  teachers: number;
}

const PLAN_TIERS = ['free', 'individual', 'school', 'trust'] as const;
const PLAN_SUB_TIERS = ['starter', 'standard', 'plus'] as const;
const BILLING_STATUSES = ['trialing', 'active', 'past_due', 'cancelled', 'paused'] as const;
const NO_SUB_TIER = '__none__';

function stripeCustomerUrl(id: string) {
  return `https://dashboard.stripe.com/customers/${id}`;
}

function stripeSubscriptionUrl(id: string) {
  return `https://dashboard.stripe.com/subscriptions/${id}`;
}

export default function AdminSchoolDetail() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Billing override state
  const [saving, setSaving] = useState(false);
  const [overrideTier, setOverrideTier] = useState('');
  const [overrideSubTier, setOverrideSubTier] = useState('');
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideTrialEndsAt, setOverrideTrialEndsAt] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // Suspension dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Internal notes state
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const notesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: school, isLoading: schoolLoading, error: schoolError, refetch: refetchSchool } = useQuery({
    queryKey: ['admin-school', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('*')
        .eq('id', schoolId!)
        .single();
      if (error) throw error;
      return data as School;
    },
    enabled: !!schoolId,
  });

  const { data: profiles = [], isLoading: profilesLoading } = useQuery({
    queryKey: ['admin-school-profiles', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('school_id', schoolId!);
      if (error) throw error;
      return data as Profile[];
    },
    enabled: !!schoolId,
  });

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['admin-school-usage', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_school_usage_as_admin', {
        p_school_id: schoolId!,
      });
      if (error) throw error;
      return data as UsageCounts;
    },
    enabled: !!schoolId,
  });

  // Pre-populate form when school loads
  useEffect(() => {
    if (school) {
      setOverrideTier(school.plan_tier);
      setOverrideSubTier(school.plan_sub_tier ?? NO_SUB_TIER);
      setOverrideStatus(school.billing_status);
      setOverrideTrialEndsAt(
        school.trial_ends_at
          ? new Date(school.trial_ends_at).toISOString().slice(0, 16)
          : ''
      );
      setNotes(school.internal_notes ?? '');
    }
  }, [school]);

  const handleSave = async () => {
    if (!schoolId) return;
    if (!overrideReason.trim()) {
      toast({ title: 'Reason required', description: 'Enter a reason for this billing override.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const before = {
      plan_tier: school?.plan_tier,
      plan_sub_tier: school?.plan_sub_tier,
      billing_status: school?.billing_status,
      trial_ends_at: school?.trial_ends_at,
    };
    const after = {
      plan_tier: overrideTier,
      plan_sub_tier: overrideSubTier === NO_SUB_TIER ? null : overrideSubTier,
      billing_status: overrideStatus,
      trial_ends_at: overrideTrialEndsAt ? new Date(overrideTrialEndsAt).toISOString() : null,
    };
    const { error } = await supabase
      .from('schools')
      .update(after)
      .eq('id', schoolId);
    if (error) {
      setSaving(false);
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
      return;
    }
    await supabase.rpc('log_admin_action', {
      p_action: 'billing_override',
      p_target_type: 'school',
      p_target_id: schoolId,
      p_metadata: { before, after },
      p_reason: overrideReason.trim(),
    });
    setSaving(false);
    setOverrideReason('');
    toast({ title: 'Billing updated', description: 'School billing override saved.' });
    refetchSchool();
  };

  const handleSuspend = async () => {
    if (!schoolId || !suspendReason.trim()) return;
    setSuspending(true);
    const { error } = await supabase
      .from('schools')
      .update({ suspended_at: new Date().toISOString(), suspension_reason: suspendReason.trim() })
      .eq('id', schoolId);
    if (error) {
      setSuspending(false);
      toast({ title: 'Suspend failed', description: error.message, variant: 'destructive' });
      return;
    }
    await supabase.rpc('log_admin_action', {
      p_action: 'school_suspend',
      p_target_type: 'school',
      p_target_id: schoolId,
      p_metadata: {},
      p_reason: suspendReason.trim(),
    });
    // Terminate all active sessions and send notification email
    supabase.functions.invoke('admin-user-mgmt', {
      body: { action: 'post_suspend_school', schoolId },
    }).catch(e => console.warn('post_suspend_school failed:', e));
    setSuspending(false);
    setSuspendDialogOpen(false);
    setSuspendReason('');
    toast({ title: 'School suspended', description: 'Sessions terminated and admin notified.' });
    refetchSchool();
  };

  const handleReactivate = async () => {
    if (!schoolId) return;
    setReactivating(true);
    const { error } = await supabase
      .from('schools')
      .update({ suspended_at: null, suspension_reason: null })
      .eq('id', schoolId);
    if (error) {
      setReactivating(false);
      toast({ title: 'Reactivate failed', description: error.message, variant: 'destructive' });
      return;
    }
    await supabase.rpc('log_admin_action', {
      p_action: 'school_reactivate',
      p_target_type: 'school',
      p_target_id: schoolId,
      p_metadata: {},
      p_reason: null,
    });
    setReactivating(false);
    toast({ title: 'School reactivated', description: 'The school has been reactivated.' });
    refetchSchool();
  };

  const handleNotesSave = async (value: string) => {
    if (!schoolId) return;
    setNotesSaving(true);
    const { error } = await supabase
      .from('schools')
      .update({ internal_notes: value || null })
      .eq('id', schoolId);
    if (!error) {
      await supabase.rpc('log_admin_action', {
        p_action: 'school_notes_updated',
        p_target_type: 'school',
        p_target_id: schoolId,
        p_metadata: {},
        p_reason: null,
      });
    }
    setNotesSaving(false);
  };

  const handleNotesBlur = () => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    handleNotesSave(notes);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (schoolLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      </DashboardLayout>
    );
  }

  if (schoolError || !school) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-2">
          <p className="text-sm font-medium text-destructive">Failed to load school.</p>
          {schoolError && <p className="text-xs text-muted-foreground">{(schoolError as Error).message}</p>}
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/schools')}>Back to schools</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/schools')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{school.name}</h1>
            <p className="text-muted-foreground text-sm">Created {formatDate(school.created_at)}</p>
          </div>
        </div>

        {/* Suspension alert */}
        {school.suspended_at && (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-destructive">This school is suspended</p>
              <p className="text-muted-foreground mt-0.5">
                Suspended {formatDate(school.suspended_at)}{school.suspension_reason ? ` — ${school.suspension_reason}` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Overview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Overview</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div><span className="text-muted-foreground">DfE number</span><p className="font-medium">{school.dfe_number ?? '—'}</p></div>
            <div><span className="text-muted-foreground">Postcode</span><p className="font-medium">{school.postcode ?? '—'}</p></div>
            <div><span className="text-muted-foreground">Billing email</span><p className="font-medium">{school.billing_email ?? '—'}</p></div>
            <div><span className="text-muted-foreground">Billing scope</span><p className="font-medium">{school.billing_scope ?? '—'}</p></div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Stripe customer</span>
              {school.stripe_customer_id ? (
                <a
                  href={stripeCustomerUrl(school.stripe_customer_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-mono text-xs text-primary hover:underline mt-0.5"
                >
                  {school.stripe_customer_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <p className="font-mono text-xs">—</p>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Stripe subscription</span>
              {school.stripe_subscription_id ? (
                <a
                  href={stripeSubscriptionUrl(school.stripe_subscription_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-mono text-xs text-primary hover:underline mt-0.5"
                >
                  {school.stripe_subscription_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <p className="font-mono text-xs">—</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Suspension controls */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Suspension</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {school.suspended_at ? (
              <div className="space-y-3">
                <p className="text-muted-foreground">
                  Suspended on {formatDate(school.suspended_at)}.{' '}
                  {school.suspension_reason && <span>Reason: {school.suspension_reason}</span>}
                </p>
                <Button
                  variant="outline"
                  onClick={handleReactivate}
                  disabled={reactivating}
                >
                  {reactivating ? 'Reactivating…' : 'Reactivate school'}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-muted-foreground">School is active. Suspending will block billing-gated operations.</p>
                <Button
                  variant="destructive"
                  onClick={() => setSuspendDialogOpen(true)}
                >
                  Suspend school
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Internal notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Internal notes
              {notesSaving && <span className="text-xs font-normal text-muted-foreground">Saving…</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Operator notes — not visible to school users"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={handleNotesBlur}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1.5">Auto-saves on blur</p>
          </CardContent>
        </Card>

        {/* Usage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Usage totals</CardTitle>
          </CardHeader>
          <CardContent>
            {usageLoading ? (
              <p className="text-sm text-muted-foreground">Loading usage…</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: Users, label: 'Pupils', value: usage?.pupils ?? 0 },
                  { icon: BookOpen, label: 'Classes', value: usage?.classes ?? 0 },
                  { icon: FileText, label: 'Assessments', value: usage?.assessments ?? 0 },
                  { icon: GraduationCap, label: 'Teachers', value: usage?.teachers ?? 0 },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex flex-col items-center gap-1 rounded-lg border p-4">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <span className="text-2xl font-bold">{value}</span>
                    <span className="text-xs text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Users ({profilesLoading ? '…' : profiles.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {profilesLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : profiles.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users found.</p>
            ) : (
              <ul className="divide-y text-sm">
                {profiles.map(p => (
                  <li key={p.id} className="py-2 flex items-center justify-between gap-4">
                    <span className="font-medium">{p.full_name ?? '—'}</span>
                    <span className="text-muted-foreground">{p.email ?? '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Billing override */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Billing override</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stripe warning */}
            <div className="flex items-start gap-2 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>DB-only override — Stripe not modified. The next Stripe webhook event may overwrite these values.</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Plan tier</Label>
                <Select value={overrideTier} onValueChange={setOverrideTier}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_TIERS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Plan sub-tier {overrideTier !== 'school' && <span className="text-muted-foreground text-xs">(school only)</span>}</Label>
                <Select
                  value={overrideSubTier}
                  onValueChange={setOverrideSubTier}
                  disabled={overrideTier !== 'school'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SUB_TIER}>— none —</SelectItem>
                    {PLAN_SUB_TIERS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Billing status</Label>
                <Select value={overrideStatus} onValueChange={setOverrideStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Trial ends at</Label>
                <div className="flex gap-2">
                  <Input
                    type="datetime-local"
                    value={overrideTrialEndsAt}
                    onChange={e => setOverrideTrialEndsAt(e.target.value)}
                    className="flex-1"
                  />
                  {overrideTrialEndsAt && (
                    <Button variant="outline" size="sm" onClick={() => setOverrideTrialEndsAt('')}>
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>Reason <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="Required — describe the reason for this override"
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving || !overrideReason.trim()}>
                {saving ? 'Saving…' : 'Save override'}
              </Button>
              <div className="text-sm text-muted-foreground">
                Current: <Badge variant="outline" className="ml-1">{school.plan_tier}</Badge>
                {school.plan_sub_tier && <Badge variant="outline" className="ml-1">{school.plan_sub_tier}</Badge>}
                <Badge variant="outline" className="ml-1">{school.billing_status}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suspend dialog */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend {school.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This will suspend the school, terminate all active user sessions immediately, and send a notification email to the school admin.
            </p>
            <div className="space-y-1.5">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea
                placeholder="Required — reason for suspension"
                value={suspendReason}
                onChange={e => setSuspendReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={suspending || !suspendReason.trim()}
            >
              {suspending ? 'Suspending…' : 'Suspend school'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
